import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '../lib/utils'
import termsOfUseMarkdown from '../data/athios_terms_of_use.md?raw'
import privacyPolicyMarkdown from '../data/athios_privacy_policy.md?raw'

interface LegalDocumentProps {
  showScrollArea?: boolean
  maxHeight?: string
  className?: string
}

const markdownComponents = {
  h1: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1
      className={cn('text-foreground font-semibold text-base mt-6 mb-2 first:mt-0', className)}
      {...props}
    />
  ),
  h2: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2 className={cn('text-foreground font-semibold text-sm mt-4 mb-2', className)} {...props} />
  ),
  h3: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 className={cn('text-foreground font-semibold text-sm mt-4 mb-2', className)} {...props} />
  ),
  p: ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className={cn('mb-3', className)} {...props} />
  ),
  ul: ({ className, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className={cn('list-disc pl-5 mb-3 space-y-1', className)} {...props} />
  ),
  li: ({ className, ...props }: React.LiHTMLAttributes<HTMLLIElement>) => (
    <li className={cn('mb-1', className)} {...props} />
  ),
  a: ({ className, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a
      className={cn('text-[#4d7c0f] hover:text-[#a8e45a] underline', className)}
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  table: ({ className, ...props }: React.TableHTMLAttributes<HTMLTableElement>) => (
    <table className={cn('w-full text-left border-collapse mb-3', className)} {...props} />
  ),
  th: ({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
    <th
      className={cn(
        'border border-foreground/10 px-2 py-1 text-foreground font-semibold',
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
    <td className={cn('border border-foreground/10 px-2 py-1 align-top', className)} {...props} />
  ),
}

const LegalDocument: React.FC<LegalDocumentProps & { content: string }> = ({
  content,
  showScrollArea = true,
  maxHeight = 'h-96',
  className,
}) => {
  const body = (
    <div className="text-gray-700 space-y-1 text-sm leading-relaxed">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  )

  return (
    <div
      className={cn(
        'rounded-2xl border border-foreground/10 bg-card p-6 shadow-[0_1px_2px_rgba(30,41,59,0.04)]',
        showScrollArea && `${maxHeight} overflow-y-auto`,
        className,
      )}
    >
      {body}
    </div>
  )
}

export const TermsOfUseContent: React.FC<LegalDocumentProps> = (props) => (
  <LegalDocument content={termsOfUseMarkdown} {...props} />
)

export const PrivacyPolicyContent: React.FC<LegalDocumentProps> = (props) => (
  <LegalDocument content={privacyPolicyMarkdown} {...props} />
)
